import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { CampoGaleria, CampoImagen } from '../componentes/Imagenes';
import { fechaCorta } from '../formato';
import { CompletaTienda } from './CompletaTienda';
import { ElegirPlantilla } from './ElegirPlantilla';
import { IdentidadTienda } from './IdentidadTienda';
import './sitio.css';
import { type Sitio } from './comercial-comun';

// --- Mi sitio --------------------------------------------------------------------------------

/** Tienda del lotero: datos de contacto, tema y publicación. */
export function MiSitio() {
  const api = useApi();
  // Claves telefónicas de los países activos (Super Admin › Países); México por omisión.
  const config = useConsulta<{
    paises: { codigo: string; nombre: string; prefijoTelefonico: string | null }[];
  }>('/platform/configuracion/publico');
  const paises = (config.datos?.paises ?? []).filter((p) => p.prefijoTelefonico);
  if (!paises.some((p) => p.prefijoTelefonico === '52'))
    paises.unshift({ codigo: 'MX', nombre: 'México', prefijoTelefonico: '52' });
  const sitio = useConsulta<Sitio>('/commerce/sitio');
  // La tienda se abre en su dominio propio verificado o, si no tiene, en su subdominio.
  const dominios = useConsulta<{ dominio: string; tipo: string; verificado: boolean }[]>(
    '/platform/mi-lotero/dominios',
  );
  const subdominio = dominios.datos?.find((d) => d.tipo === 'subdominio')?.dominio;
  const dominioTienda =
    dominios.datos?.find((d) => d.tipo === 'propio' && d.verificado)?.dominio ?? subdominio;
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const s = sitio.datos;
  const sinConfigurar = sitio.estadoError === 404;

  const guardar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    const texto = (k: string) => {
      const v = String(d.get(k) ?? '').trim();
      return v ? { [k]: v } : {};
    };
    setError(null);
    setAviso(null);
    try {
      await api('PUT', '/commerce/sitio', {
        nombreComercial: d.get('nombreComercial'),
        ciudad: d.get('ciudad'),
        ...texto('lema'),
        ...texto('descripcion'),
        ...texto('estadoRegion'),
        ...texto('telefono'),
        pais: String(d.get('pais') || 'MX'),
        telefonoWhatsapp: d.get('telefonoWhatsapp') === 'si',
        telefonoPrefijo: String(d.get('telefonoPrefijo') || '52'),
        whatsappPrefijo: String(d.get('whatsappPrefijo') || '52'),
        ...texto('whatsapp'),
        ...texto('correo'),
        ...texto('logoUrl'),
        ...texto('portadaUrl'),
        ...texto('logoImagen'),
        ...texto('portadaImagen'),
        galeria: JSON.parse(String(d.get('galeria') || '[]')) as string[],
        ...texto('horario'),
        ...texto('direccion'),
        ...texto('enfoque'),
        colores: { primario: d.get('primario'), secundario: d.get('secundario') },
      });
      setAviso('Guardado');
      sitio.recargar();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const publicar = async () => {
    setError(null);
    try {
      await api('POST', '/commerce/sitio/publicar');
      setAviso('Tu tienda está publicada');
      sitio.recargar();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (sitio.error && !sinConfigurar) return <p className="error">{sitio.error}</p>;
  if (!s && !sinConfigurar) return <p className="subtitulo">Cargando…</p>;
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Mi sitio</h1>
          <div className="subtitulo">
            {s?.publicado
              ? `Publicado desde el ${fechaCorta(s.publicadoEn)} en ${dominioTienda ?? s.slug ?? 'tu subdominio'}`
              : 'Tu tienda aún no está publicada'}
          </div>
        </div>
        <div className="relleno" />
        {s && !s.publicado && (
          <button className="boton primario" onClick={() => void publicar()}>
            Publicar tienda
          </button>
        )}
        {s?.publicado && dominioTienda && (
          <a
            className="boton"
            href={`https://${dominioTienda}/`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ver mi tienda
          </a>
        )}
        <Link className="boton" to="/dominios">
          Mi dominio
        </Link>
      </div>
      {error && <p className="error">{error}</p>}
      {aviso && <p className="etiqueta exito">{aviso}</p>}
      {s && <CompletaTienda sitio={s} />}
      {/* La vista previa va por el subdominio: la política de seguridad del Admin solo permite
          incrustar subdominios de la plataforma, no el dominio propio. */}
      {s && <ElegirPlantilla sitio={s} dominio={subdominio} alCambiar={sitio.recargar} />}
      <form id="datos" className="seccion cuerpo formulario" onSubmit={(e) => void guardar(e)}>
        {(
          [
            ['nombreComercial', 'Nombre comercial', s?.nombreComercial, true],
            ['lema', 'Lema', s?.lema, false],
            ['ciudad', 'Ciudad', s?.ciudad, true],
            ['estadoRegion', 'Estado', s?.estadoRegion, false],
            ['correo', 'Correo', s?.correo, false],
            ['horario', 'Horario (p. ej. Lun a sáb 9:00 a 18:00)', s?.horario, false],
            ['direccion', 'Dirección (se abre en el mapa)', s?.direccion, false],
          ] as const
        ).map(([nombre, rotulo, valor, requerido]) => (
          <label key={nombre} className="campo">
            <span>{rotulo}</span>
            <input name={nombre} defaultValue={valor ?? ''} required={requerido} />
          </label>
        ))}
        <CampoImagen
          uso="logo"
          rotulo="Logotipo"
          ayuda="PNG con fondo transparente o JPG cuadrado; se ve en el encabezado de tu tienda."
          nombre="logoImagen"
          nombreLegado="logoUrl"
          inicial={s?.logoImagen}
          legado={s?.logoImagen ? null : s?.logoUrl}
        />
        <CampoImagen
          uso="portada"
          rotulo="Foto de portada"
          ayuda="Una foto horizontal de tu negocio, tu taller o tus autos (mínimo 1600 px de ancho para que se vea nítida)."
          nombre="portadaImagen"
          nombreLegado="portadaUrl"
          inicial={s?.portadaImagen}
          legado={s?.portadaImagen ? null : s?.portadaUrl}
        />
        <CampoGaleria
          uso="galeria"
          rotulo="Galería de tu negocio"
          ayuda="Fotos de tu local, tu equipo y tus entregas: dan confianza aunque todavía no tengas inventario."
          nombre="galeria"
          inicial={s?.galeriaIds ?? []}
          maximo={12}
        />
        <ContactoTienda s={s} paises={paises} />
        <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <input
            type="checkbox"
            name="telefonoWhatsapp"
            value="si"
            defaultChecked={s?.telefonoWhatsapp ?? false}
            style={{ minHeight: 0 }}
          />
          <span>El teléfono tiene WhatsApp (en tu tienda abre WhatsApp en lugar de llamar)</span>
        </label>
        <label className="campo">
          <span>Con qué abre tu tienda</span>
          <select name="enfoque" defaultValue={s?.enfoque ?? ''}>
            <option value="">Según lo que más publicas</option>
            <option value="autos">Autos (lotero)</option>
            <option value="piezas">Piezas por vehículo (yonkero)</option>
            <option value="mixto">Autos y piezas</option>
          </select>
        </label>
        <label className="campo">
          <span>Descripción</span>
          <textarea
            name="descripcion"
            defaultValue={s?.descripcion ?? ''}
            rows={3}
            maxLength={2000}
          />
        </label>
        <div style={{ display: 'flex', gap: 16 }}>
          <label className="campo">
            <span>Color de tu marca</span>
            <input type="color" name="primario" defaultValue={s?.colores?.primario ?? '#0b3d91'} />
          </label>
          <label className="campo">
            <span>Color secundario</span>
            <input
              type="color"
              name="secundario"
              defaultValue={s?.colores?.secundario ?? '#f5a623'}
            />
          </label>
        </div>
        <div>
          <button className="boton primario">Guardar</button>
        </div>
      </form>
      {s && <IdentidadTienda key={s.slug} sitio={s} alGuardar={sitio.recargar} />}
    </>
  );
}

type PaisTel = { codigo: string; nombre: string; prefijoTelefonico: string | null };

/**
 * País de la tienda y sus números (arquitectura 2.19). La clave telefónica de cada número se
 * preselecciona con la del país de la tienda y se puede cambiar (un negocio en México puede tener
 * un teléfono de EE. UU.); si cambia el país, siguen al nuevo solo las claves que no se tocaron.
 */
function ContactoTienda({ s, paises }: { s: Sitio | null | undefined; paises: PaisTel[] }) {
  const claveDe = (codigo: string) =>
    paises.find((p) => p.codigo === codigo)?.prefijoTelefonico ?? '52';
  const [pais, setPais] = useState(s?.pais ?? 'MX');
  const [claves, setClaves] = useState({
    telefono: s?.telefonoPrefijo ?? claveDe(s?.pais ?? 'MX'),
    whatsapp: s?.whatsappPrefijo ?? claveDe(s?.pais ?? 'MX'),
  });
  const [tocadas, setTocadas] = useState({
    telefono: Boolean(s?.telefonoPrefijo && s.telefonoPrefijo !== claveDe(s?.pais ?? 'MX')),
    whatsapp: Boolean(s?.whatsappPrefijo && s.whatsappPrefijo !== claveDe(s?.pais ?? 'MX')),
  });
  const cambiarPais = (codigo: string) => {
    setPais(codigo);
    const clave = claveDe(codigo);
    setClaves((c) => ({
      telefono: tocadas.telefono ? c.telefono : clave,
      whatsapp: tocadas.whatsapp ? c.whatsapp : clave,
    }));
  };
  // Claves únicas para el selector (EE. UU. y Canadá comparten +1).
  const opciones = paises.filter(
    (p, i) => paises.findIndex((q) => q.prefijoTelefonico === p.prefijoTelefonico) === i,
  );
  return (
    <>
      <label className="campo">
        <span>País de la tienda</span>
        <select name="pais" value={pais} onChange={(e) => cambiarPais(e.target.value)}>
          {paises.map((p) => (
            <option key={p.codigo} value={p.codigo}>
              {p.nombre}
            </option>
          ))}
        </select>
      </label>
      {(
        [
          ['telefono', 'Teléfono', s?.telefono, 'telefonoPrefijo'],
          ['whatsapp', 'WhatsApp', s?.whatsapp, 'whatsappPrefijo'],
        ] as const
      ).map(([nombre, rotulo, valor, campoClave]) => (
        <div key={nombre} className="campo">
          <span>{rotulo}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <select
              name={campoClave}
              aria-label={`Clave de país de ${rotulo.toLowerCase()}`}
              value={claves[nombre]}
              onChange={(e) => {
                setClaves((c) => ({ ...c, [nombre]: e.target.value }));
                setTocadas((t) => ({ ...t, [nombre]: true }));
              }}
              style={{ width: 'auto' }}
            >
              {opciones.map((p) => (
                <option key={p.codigo} value={p.prefijoTelefonico ?? ''}>
                  +{p.prefijoTelefonico}
                </option>
              ))}
            </select>
            <input name={nombre} defaultValue={valor ?? ''} inputMode="tel" style={{ flex: 1 }} />
          </div>
        </div>
      ))}
    </>
  );
}
